/**
 * The review session as an APPEND-ONLY episodic event log (business/knowledge
 * `memory-and-knowledge-topology.md`: "event append; corrections supersede").
 *
 * A session is never mutated in place — every reviewer action is one immutable
 * {@link SessionEvent}, and the current picture is a PURE FOLD ({@link sessionState})
 * over the log. Corrections supersede rather than overwrite: an annotation is
 * withdrawn by an `annotation.Superseded` event (the original stays in the log);
 * a re-grade of the same (subject, dimension) is a later `grade.Recorded` that
 * wins on fold. This keeps the review auditable and the signal history intact.
 *
 * Every event validator follows the draft pattern — the union {@link SessionEvent}
 * is the append-time gate (validate an incoming event, never cast it into the log).
 */

import { type } from "arktype"
import {
  AnnotationDraft,
  DimensionGrade,
  PairwiseChoice,
  TasteRuling,
  type Annotation,
  type AnnotationOf,
  type DimensionGradeOf,
} from "./vocabulary"

/** Common envelope on every event: when it happened and who did it. */
const meta = { at: "string.date.iso", actor: "string >= 1" } as const

/** A reviewer added a new annotation (carries the assigned id + the draft). */
export const AnnotationAdded = type({
  type: "'annotation.Added'",
  ...meta,
  id: "string >= 1",
  draft: AnnotationDraft,
})
export type AnnotationAdded = typeof AnnotationAdded.infer

/** A reviewer withdrew an earlier annotation (correction supersedes; log is kept). */
export const AnnotationSuperseded = type({
  type: "'annotation.Superseded'",
  ...meta,
  /** The id of the annotation being withdrawn. */
  id: "string >= 1",
  /** Optional id of the annotation that replaces it (a re-mark of the same span). */
  "replacedBy?": "string",
  "reason?": "string",
})
export type AnnotationSuperseded = typeof AnnotationSuperseded.infer

/** A reviewer recorded (or re-recorded) a dimension grade for a subject. */
export const GradeRecorded = type({
  type: "'grade.Recorded'",
  ...meta,
  grade: DimensionGrade,
})
export type GradeRecorded = typeof GradeRecorded.infer

/** A reviewer recorded a pairwise preference between two subjects. */
export const PreferenceRecorded = type({
  type: "'preference.Recorded'",
  ...meta,
  choice: PairwiseChoice,
})
export type PreferenceRecorded = typeof PreferenceRecorded.infer

/** A reviewer ruled on an open taste question. */
export const RulingRecorded = type({
  type: "'ruling.Recorded'",
  ...meta,
  ruling: TasteRuling,
})
export type RulingRecorded = typeof RulingRecorded.infer

/** The session was closed with an optional verdict + summary. */
export const SessionClosed = type({
  type: "'session.Closed'",
  ...meta,
  "verdict?": "string",
  "summary?": "string",
})
export type SessionClosed = typeof SessionClosed.infer

/** The discriminated union of every review-session event. The append-time validator. */
export const SessionEvent = AnnotationAdded.or(AnnotationSuperseded)
  .or(GradeRecorded)
  .or(PreferenceRecorded)
  .or(RulingRecorded)
  .or(SessionClosed)
export type SessionEvent = typeof SessionEvent.infer

/**
 * The current picture folded from an event log. Annotations are the ACTIVE
 * (non-superseded) marks in insertion order; grades/preferences/rulings are the
 * latest recorded per key. Generic over the consumer's gate + dimension unions.
 */
export interface SessionState<Gate extends string = string, Dimension extends string = string> {
  readonly annotations: ReadonlyArray<AnnotationOf<Gate>>
  readonly grades: ReadonlyArray<DimensionGradeOf<Dimension>>
  readonly preferences: readonly PairwiseChoice[]
  readonly rulings: readonly TasteRuling[]
  readonly closed: boolean
  readonly closedAt?: string
  readonly verdict?: string
  readonly summary?: string
}

/** Stable key for a preference: the ordered pair it compares. */
const pairKey = (choice: PairwiseChoice): string => `${choice.leftRef} ${choice.rightRef}`

/**
 * Fold an event log into the current {@link SessionState}. Pure — same input,
 * same output, no mutation of the events. Supersede semantics: `annotation.Added`
 * inserts, `annotation.Superseded` drops from the active set (the log still holds
 * both); grades/preferences/rulings are latest-wins per key.
 */
export function sessionState<Gate extends string = string, Dimension extends string = string>(
  events: readonly SessionEvent[],
): SessionState<Gate, Dimension> {
  const annotations = new Map<string, Annotation>()
  const grades = new Map<string, DimensionGrade>()
  const preferences = new Map<string, PairwiseChoice>()
  const rulings = new Map<string, TasteRuling>()
  let closed = false
  let closedAt: string | undefined
  let verdict: string | undefined
  let summary: string | undefined

  for (const event of events) {
    switch (event.type) {
      case "annotation.Added":
        annotations.set(event.id, {
          ...event.draft,
          id: event.id,
          resolved: false,
          createdAt: event.at,
          actor: event.actor,
        })
        break
      case "annotation.Superseded":
        annotations.delete(event.id)
        break
      case "grade.Recorded":
        grades.set(`${event.grade.subjectRef} ${event.grade.dimension}`, event.grade)
        break
      case "preference.Recorded":
        preferences.set(pairKey(event.choice), event.choice)
        break
      case "ruling.Recorded":
        rulings.set(event.ruling.questionId, event.ruling)
        break
      case "session.Closed":
        closed = true
        closedAt = event.at
        verdict = event.verdict
        summary = event.summary
        break
    }
  }

  // The Gate/Dimension parameters are a CONSUMER PROMISE about their own gate/
  // dimension vocabulary — the fold produces validated `string`-gated values, and
  // the caller asserts they fall in its union. This variance seam is the one place
  // that assertion is made (mirrors elicitation-core's construct-target narrowing).
  return {
    annotations: [...annotations.values()] as unknown as ReadonlyArray<AnnotationOf<Gate>>,
    grades: [...grades.values()] as unknown as ReadonlyArray<DimensionGradeOf<Dimension>>,
    preferences: [...preferences.values()],
    rulings: [...rulings.values()],
    closed,
    closedAt,
    verdict,
    summary,
  }
}
